import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicShell from "@/components/public/PublicShell";
import { ArrowRight, Sparkles, Search as SearchIcon } from "lucide-react";
import { CATEGORY_CATALOG, listArticles } from "@/data/vault_articles";

/**
 * VAULT Learn — public education hub.
 *
 * Category filter is URL-driven: `?category=<slug>` (and optional
 * `&q=<search>`). Selecting a category updates the URL via replace(),
 * so refresh + back/forward preserve state. All articles are published
 * client-side fixtures for now — the `listArticles()` helper hides that.
 */
export default function LearnHome() {
    const [sp, setSp] = useSearchParams();
    const category = sp.get("category") || "latest";
    const query = sp.get("q") || "";

    const articles = useMemo(() => listArticles({ category, query }), [category, query]);
    const activeMeta = CATEGORY_CATALOG.find((c) => c.slug === category) || CATEGORY_CATALOG[0];

    const setCategory = (slug) => {
        const next = new URLSearchParams(sp);
        if (slug === "latest") next.delete("category");
        else next.set("category", slug);
        setSp(next, { replace: false });
        // Focus grid after selection so screen readers announce it.
        requestAnimationFrame(() => {
            const grid = document.querySelector("[data-testid='learn-grid']");
            if (grid && grid.scrollIntoView) grid.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    const setQuery = (q) => {
        const next = new URLSearchParams(sp);
        if (q) next.set("q", q); else next.delete("q");
        setSp(next, { replace: true });
    };

    return (
        <PublicShell>
            <div className="lh-root" data-testid="learn-home">
                <style>{LEARN_CSS}</style>

                {/* Hero */}
                <section className="lh-container lh-hero" data-testid="learn-hero">
                    <div className="lh-eyebrow">
                        <Sparkles size={12} strokeWidth={2.2} /> THE VAULT JOURNAL
                    </div>
                    <h1 className="lh-title">Learn how to organise your financial life.</h1>
                    <p className="lh-lede">
                        Clear, editorial reading on the parts of money that most software ignores —
                        organisation, obligations, documents, and the quiet habits that keep families ahead.
                    </p>
                </section>

                {/* Search */}
                <section className="lh-container lh-search-row">
                    <div className="lh-search" role="search">
                        <SearchIcon size={14} className="lh-search__icon" aria-hidden="true" />
                        <input
                            type="search"
                            aria-label="Search VAULT Journal"
                            placeholder={`Search ${activeMeta.label.toLowerCase()}…`}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            data-testid="learn-search"
                        />
                        {query && (
                            <button className="lh-search__clear" onClick={() => setQuery("")} aria-label="Clear search" data-testid="learn-search-clear">Clear</button>
                        )}
                    </div>
                </section>

                {/* Category rail */}
                <nav className="lh-container lh-cats" aria-label="Article categories" data-testid="learn-categories">
                    <div className="lh-cats__scroll" role="tablist">
                        {CATEGORY_CATALOG.map((c) => {
                            const isActive = c.slug === activeMeta.slug;
                            return (
                                <button
                                    key={c.slug}
                                    role="tab"
                                    aria-selected={isActive}
                                    aria-current={isActive ? "page" : undefined}
                                    tabIndex={isActive ? 0 : -1}
                                    className={`lh-chip ${isActive ? "is-active" : ""}`}
                                    type="button"
                                    onClick={() => setCategory(c.slug)}
                                    data-testid={`learn-cat-${c.slug}`}
                                >
                                    {c.label}
                                </button>
                            );
                        })}
                    </div>
                </nav>

                {/* Article grid or empty */}
                <section className="lh-container" data-testid="learn-grid" aria-live="polite">
                    {articles.length === 0 ? (
                        <div className="lh-empty" data-testid="learn-empty">
                            <div className="lh-empty__eyebrow">{activeMeta.label.toUpperCase()}</div>
                            <div className="lh-empty__title">More from VAULT is coming here.</div>
                            <p className="lh-empty__body">
                                We&rsquo;re publishing new {activeMeta.label} pieces regularly. In the
                                meantime, explore the latest across the journal.
                            </p>
                            <button className="lh-empty__cta" onClick={() => setCategory("latest")} data-testid="learn-empty-latest">
                                Explore Latest <ArrowRight size={13}/>
                            </button>
                        </div>
                    ) : (
                        <div className="lh-grid">
                            {articles.map((a) => (
                                <article key={a.slug} className="lh-card" data-testid={`article-${a.slug}`}>
                                    <div className="lh-card__cat">{(a.category || "").toUpperCase()}</div>
                                    <h2 className="lh-card__title">
                                        <Link to={`/learn/articles/${a.slug}`} className="lh-card__titlelink" data-testid={`article-title-${a.slug}`}>
                                            {a.title}
                                        </Link>
                                    </h2>
                                    <p className="lh-card__teaser">{a.deck || a.teaser}</p>
                                    <div className="lh-card__foot">
                                        <span className="lh-card__time">{a.reading_time || "6 min read"}</span>
                                        <Link to={`/learn/articles/${a.slug}`} className="lh-card__link" data-testid={`read-${a.slug}`}>
                                            Read article <ArrowRight size={12} />
                                        </Link>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </section>

                <section className="lh-container lh-notice" data-testid="learn-notice">
                    <div className="lh-notice__box">
                        <div className="lh-notice__eyebrow">JOURNAL EXPANSION IN PROGRESS</div>
                        <p className="lh-notice__body">
                            We&rsquo;re publishing 20 original editorial articles, five long-form guides,
                            and the Money Minute short-read library across this quarter. Full drafts land
                            per the VAULT Journal editorial calendar — check back soon.
                        </p>
                        <Link to="/signup" className="lh-notice__cta" data-testid="learn-cta-signup">
                            Create Your VAULT <ArrowRight size={14} />
                        </Link>
                    </div>
                </section>
            </div>
        </PublicShell>
    );
}

const LEARN_CSS = `
.lh-root { background: #FBF7EE; color: #0F1E3A; min-height: 100vh; padding-bottom: 96px; }
.lh-container { max-width: 1160px; margin: 0 auto; padding: 0 28px; }
.lh-hero { padding: 72px 28px 24px; text-align: left; max-width: 900px; }
.lh-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 18px; }
.lh-title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(38px, 6vw, 68px); font-weight: 500; letter-spacing: -0.025em; line-height: 1.02; margin: 0 0 20px; color: #0F1E3A; }
.lh-lede { font-size: 17px; line-height: 1.6; color: #57534E; max-width: 620px; margin: 0; }
.lh-search-row { padding: 8px 28px 12px; }
.lh-search { display: inline-flex; align-items: center; gap: 8px; background: #fff; border: 1px solid rgba(15,30,58,0.14); border-radius: 999px; padding: 8px 14px; max-width: 420px; width: 100%; box-shadow: 0 6px 18px -12px rgba(15,30,58,0.18); }
.lh-search__icon { color: #78716C; flex-shrink: 0; }
.lh-search input { border: none; outline: none; background: transparent; flex: 1; font-size: 13.5px; color: #0F1E3A; min-width: 0; }
.lh-search input::placeholder { color: #A29D95; }
.lh-search__clear { border: none; background: none; color: #57534E; font-size: 12px; cursor: pointer; padding: 2px 6px; }
.lh-search__clear:hover { color: #0F1E3A; }
.lh-cats { padding: 4px 28px 22px; }
.lh-cats__scroll { display: flex; gap: 8px; flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; padding-bottom: 4px; scrollbar-width: none; }
.lh-cats__scroll::-webkit-scrollbar { display: none; }
@media (min-width: 900px) { .lh-cats__scroll { flex-wrap: wrap; overflow-x: visible; } }
.lh-chip { padding: 10px 16px; border-radius: 999px; border: 1px solid rgba(15,30,58,0.14); background: #FFFFFF; font-size: 12.5px; font-weight: 500; letter-spacing: 0.01em; color: #57534E; cursor: pointer; transition: color .18s cubic-bezier(0.16,1,0.3,1), background-color .18s cubic-bezier(0.16,1,0.3,1), border-color .18s cubic-bezier(0.16,1,0.3,1), box-shadow .18s cubic-bezier(0.16,1,0.3,1); white-space: nowrap; min-height: 40px; }
.lh-chip:hover { color: #0F1E3A; border-color: rgba(15,30,58,0.28); }
.lh-chip:focus-visible { outline: 2px solid #C6A86B; outline-offset: 2px; }
.lh-chip.is-active { background: #0F1E3A; color: #F6F3EC; border-color: #0F1E3A; box-shadow: 0 8px 18px -8px rgba(15,30,58,0.35); }
.lh-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; padding: 8px 0 0; }
@media (max-width: 900px) { .lh-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px) { .lh-grid { grid-template-columns: 1fr; } }
.lh-card { display: flex; flex-direction: column; padding: 24px 22px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 20px; transition: transform .24s cubic-bezier(0.16,1,0.3,1), box-shadow .24s cubic-bezier(0.16,1,0.3,1), border-color .24s cubic-bezier(0.16,1,0.3,1); }
.lh-card:hover { transform: translateY(-4px); box-shadow: 0 24px 48px -20px rgba(15,30,58,0.18); border-color: rgba(198,168,107,0.35); }
.lh-card__cat { font-size: 10px; letter-spacing: 0.24em; font-weight: 700; color: #C6A86B; margin-bottom: 12px; }
.lh-card__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 22px; font-weight: 500; letter-spacing: -0.01em; color: #0F1E3A; margin: 0 0 10px; line-height: 1.15; }
.lh-card__titlelink { color: inherit; text-decoration: none; }
.lh-card__titlelink:hover { color: #C6A86B; }
.lh-card__teaser { font-size: 14px; line-height: 1.55; color: #57534E; margin: 0 0 20px; flex: 1; }
.lh-card__foot { display: flex; align-items: center; justify-content: space-between; padding-top: 12px; border-top: 1px solid rgba(15,30,58,0.06); }
.lh-card__time { font-size: 12px; color: #78716C; }
.lh-card__link { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 600; color: #0F1E3A; text-decoration: none; }
.lh-card__link:hover { color: #C6A86B; }
.lh-empty { padding: 48px 28px 16px; border: 1px dashed rgba(15,30,58,0.18); border-radius: 20px; background: #fff; text-align: center; }
.lh-empty__eyebrow { font-size: 10.5px; letter-spacing: 0.24em; font-weight: 700; color: #C6A86B; margin-bottom: 8px; }
.lh-empty__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 26px; color: #0F1E3A; margin-bottom: 8px; }
.lh-empty__body { font-size: 14px; color: #57534E; max-width: 520px; margin: 0 auto 20px; line-height: 1.55; }
.lh-empty__cta { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 999px; background: #0F1E3A; color: #F6F3EC; font-size: 13px; font-weight: 600; border: none; cursor: pointer; }
.lh-empty__cta:hover { background: #C6A86B; color: #0F1E3A; }
.lh-notice { padding: 56px 28px 0; }
.lh-notice__box { background: linear-gradient(140deg, #0F1E3A 0%, #0A1424 100%); color: #F1F5F9; padding: 36px 32px; border-radius: 24px; text-align: center; border: 1px solid rgba(198,168,107,0.22); }
.lh-notice__eyebrow { font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #E6D5A8; margin-bottom: 10px; }
.lh-notice__body { font-size: 15px; line-height: 1.6; color: rgba(241,245,249,0.85); max-width: 620px; margin: 0 auto 22px; }
.lh-notice__cta { display: inline-flex; align-items: center; gap: 8px; padding: 12px 22px; border-radius: 999px; background: linear-gradient(180deg, #E6D5A8, #C6A86B); color: #0F1E3A; font-size: 14px; font-weight: 600; text-decoration: none; box-shadow: 0 12px 26px -8px rgba(198,168,107,0.55); transition: transform .18s cubic-bezier(0.16,1,0.3,1); }
.lh-notice__cta:hover { transform: translateY(-1px); }
`;
